import { containsTrueOrFalse } from '@dte/otw-utils-core/src/boolean/containsTrueOrFalse';
import { filterEmpty } from '@dte/otw-utils-core/src/lists/filterEmpty';
import { includesAny } from '@dte/otw-utils-core/src/lists/includesAny';
import { getArray } from '@dte/otw-utils-core/src/lists/mergeArrays';
import { copyJson } from '@dte/otw-utils-core/src/objects/copyJson';
import { filterRecord, mapRecord } from '@dte/otw-utils-core/src/objects/filterRecord';
import { isEmptyArray, isNotEmpty } from '@dte/otw-utils-core/src/objects/isEmpty';
import { containsIgnoreCase } from '@dte/otw-utils-core/src/strings/equalsIgnoreCase';
import type { CustomerReportDataV3, ReportAnswers, ReportAnswerValue, ReportAnswerValues } from './CustomerReport';
import type { QuestionAnswerPairing } from './questions/LinkedQuestions';
import { type ReportQuestion, StandardAnswerValue } from './questions/ReportQuestion';

function getFilteredAnswer(
	answerValues: ReportAnswerValues | undefined,
	answeredCategories: string[] | undefined,
): ReportAnswerValues {
	if (!answerValues) {
		return {};
	}

	const filteredAnswerValues = filterRecord(answerValues, (answerValue) =>
		includesAny(answeredCategories, answerValue?.categories),
	);

	return filteredAnswerValues;
}

export function getFilteredReportAnswers(report: Partial<CustomerReportDataV3> | undefined): ReportAnswers {
	// No report
	if (!report) {
		return {};
	}

	const answers = copyJson(report.answers);

	// No answers
	if (!answers) {
		return {};
	}

	// See which categories we've answered
	const answeredCategories = report.answeredCategories;

	const filteredAnswers: ReportAnswers = {};

	const keys = Object.keys(answers) as ReportQuestion[];
	for (const question of keys) {
		const values = answers[question];
		const filteredValues = getFilteredAnswer(values, answeredCategories);
		if (isNotEmpty(filteredValues)) {
			filteredAnswers[question] = filteredValues;
		}
	}

	return filteredAnswers;
}

/**
 * Shortcut to get the answer to a specific question
 *
 * @param report
 * @param question
 * @returns
 */
export function getAnswerValues(answers: ReportAnswers | undefined, question: ReportQuestion): ReportAnswerValues {
	if (!answers) {
		return {};
	}

	const answerValues = answers[question] ?? {};
	answers[question] = answerValues;

	return answerValues;
}

export function setAnswerValue(
	answers: ReportAnswers | undefined,
	question: ReportQuestion,
	answerKey: string = StandardAnswerValue.Value,
	answerValue: ReportAnswerValue = {},
): void {
	if (!answers) {
		return;
	}
	const answerValues = getAnswerValues(answers, question);
	answerValues[answerKey] = answerValue;
}

export function getSelectedAnswerValues(answers: ReportAnswers, question: ReportQuestion): ReportAnswerValues {
	let answerValues = getAnswerValues(answers, question);
	answerValues = filterRecord(answerValues, (value) => Boolean(value?.selected));
	return answerValues;
}

export function getAnswerValue(
	answers: ReportAnswers,
	question: ReportQuestion,
	answerKey: string = StandardAnswerValue.Value,
): ReportAnswerValue {
	const answerValues = getAnswerValues(answers, question);
	const answerValue = answerValues[answerKey] ?? {};
	answerValues[answerKey] = answerValue;
	return answerValue;
}

export function getSelectedAnswerString(
	answers: ReportAnswers,
	question: ReportQuestion,
	answerKey?: string,
): string | undefined {
	const answerValue = getAnswerValue(answers, question, answerKey);

	// Don't return the value if not selected
	if (!answerValue?.selected) {
		return undefined;
	}

	// For custom answers return the value
	if (answerValue.custom) {
		return answerValue.value;
	}

	// For freeform answers, return the value
	return answerKey;
}

export function getSelectedAnswerStrings(
	answers: ReportAnswers,
	question: ReportQuestion,
	defaultValue: string[] = [],
): string[] {
	const selectedValues = getSelectedAnswerValues(answers, question);
	const keys = Object.keys(selectedValues);

	const values = keys.map((key) => getSelectedAnswerString(answers, question, key));
	const filtered = filterEmpty(values);

	// Sometimes we may want to return a different value when the list is empty
	if (isEmptyArray(filtered)) {
		return defaultValue;
	}

	return filtered;
}

/**
 * Just get the first string is selected
 *
 * @param answers
 * @param question
 * @returns
 */
export function getFirstSelectedAnswerString(answers: ReportAnswers, question: ReportQuestion): string | undefined {
	const stringValues = getSelectedAnswerStrings(answers, question);
	return stringValues?.[0];
}

export function isAnswerValueSelected(
	answers: ReportAnswers | undefined,
	question: ReportQuestion,
	keys: string | string[] | undefined,
): boolean | undefined {
	const answerKeys = getArray(keys);

	let answerValues = getAnswerValues(answers, question);

	// Limit to the entries where either the key or the custom value matches
	answerValues = filterRecord(answerValues, (value, key) => {
		// Key matches
		if (containsIgnoreCase(answerKeys, key)) {
			return true;
		}

		// Value matches
		return containsIgnoreCase(answerKeys, value.value);
	});

	const selections = mapRecord(answerValues, (value) => value?.selected);

	// Return true, false, or undefined based on the selections
	return containsTrueOrFalse(selections);
}

export function isAnyAnswerValueSelected(
	answers: ReportAnswers,
	questions: QuestionAnswerPairing[],
): boolean | undefined {
	if (!answers) {
		return undefined;
	}
	if (!questions) {
		return undefined;
	}

	const selections = questions.map(([questionName, answerKey]) =>
		isAnswerValueSelected(answers, questionName, answerKey),
	);

	// Return true, false, or undefined based on the selections
	return containsTrueOrFalse(selections);
}

export function isQuestionAnswered(answers: ReportAnswers, question: ReportQuestion): boolean {
	const answerValues = getAnswerValues(answers, question);
	const selections = mapRecord(answerValues, (value) => value?.selected);

	// True if we have selections that are either true or false
	return containsTrueOrFalse(selections) !== undefined;
}
